import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import logger from '../utils/logger';

const MOBILE_BREAKPOINT = 700;


function Checkout() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  // Cart summary state for right-side panel
  const [cartItems, setCartItems] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  const [form, setForm] = useState({
    fullName: user?.full_name || '',
    phoneNumber: user?.mobile_number || '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'India',
    isDefault: false
  });

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/aastrog_cart/cart/addresses');
      if (res.data.success) {
        setAddresses(res.data.data || []);
        if ((res.data.data || []).length > 0) {
          const def = (res.data.data || []).find(a => a.IsDefault) || res.data.data[0];
          setSelectedAddressId(def?.AddressId || res.data.data[0].AddressId);
        }
      } else {
        setError('Failed to load addresses');
      }
    } catch (err) {
      logger.error('❌ fetchAddresses error', err);
      setError(err.response?.data?.error || 'Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCartForSummary = async () => {
    try {
      setSummaryLoading(true);
      const res = await api.get(`/aastrog_cart/cart/items?userId=${user?.userId || ''}`);
      if (res.data?.success) {
        setCartItems(res.data.data || []);
      } else {
        setCartItems([]);
      }
    } catch (err) {
      logger.error('❌ fetchCartForSummary', err);
      setCartItems([]);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !user.userId) {
      navigate('/');
      return;
    }
    fetchAddresses();
    fetchCartForSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Basic validation
    if (!form.fullName || !form.phoneNumber || !form.addressLine1 || !form.city || !form.state || !form.postalCode) {
      setError('Please fill all required fields');
      setSaving(false);
      return;
    }

    try {
      let res;

      if (editingAddressId) {
        // Edit existing address
        res = await api.put(`/aastrog_cart/cart/addresses/${editingAddressId}`, {
          fullName: form.fullName,
          phoneNumber: form.phoneNumber,
          addressLine1: form.addressLine1,
          addressLine2: form.addressLine2,
          city: form.city,
          state: form.state,
          postalCode: form.postalCode,
          country: form.country,
          isDefault: form.isDefault ? 1 : 0
        });
      } else {
        // Add new address
        res = await api.post('/aastrog_cart/cart/addresses', {
          fullName: form.fullName,
          phoneNumber: form.phoneNumber,
          addressLine1: form.addressLine1,
          addressLine2: form.addressLine2,
          city: form.city,
          state: form.state,
          postalCode: form.postalCode,
          country: form.country,
          isDefault: form.isDefault ? 1 : 0
        });
      }

      if (res.data.success) {
        setShowAddForm(false);
        setEditingAddressId(null);
        setForm(prev => ({ ...prev, addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', isDefault: false }));
        await fetchAddresses();
        // select the newly saved/edited address
        const savedId = res.data.data?.AddressId;
        if (savedId) setSelectedAddressId(savedId);
      } else {
        setError(res.data.error || 'Failed to save address');
      }
    } catch (err) {
      logger.error('❌ saveAddress error', err);
      setError(err.response?.data?.error || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      setError('Please select or add an address to continue');
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      setError('Your cart is empty');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Calculate totals
      const subtotal = cartItems.reduce((sum, item) => sum + (item.Price * item.Quantity), 0);
      const tax = subtotal * 0.18; // 18% GST
      const shippingCharge = 0; // Free shipping
      const discountAmount = 0; // No discount for now
      const totalAmount = subtotal + tax + shippingCharge - discountAmount;

      // Get selected address details for customer info
      const selectedAddress = addresses.find(addr => addr.AddressId === selectedAddressId);

      logger.log('📦 Creating order...', {
        addressId: selectedAddressId,
        totalAmount: totalAmount.toFixed(2),
        itemCount: cartItems.length
      });

      const redirectUrl = window.location.origin;

      // Create order via checkout process endpoint
      const res = await api.post('/aastrog_cart/checkout/process', {
        addressId: selectedAddressId,
        shippingCharge,
        discountAmount,
        customerName: selectedAddress?.FullName || user?.full_name || '',
        customerEmail: user?.email || '',
        customerPhone: selectedAddress?.PhoneNumber || user?.mobile_number || '',
        redirectUrl
      });

      if (res.data.success) {
        const { order, payment } = res.data.data;
        
        logger.log('✅ Order created successfully', {
          orderId: order.orderId,
          orderNumber: order.orderNumber,
          totalAmount: order.totalAmount
        });

        // Prepare PayU payment form data
        const payuData = {
          key: payment.key,
          txnid: payment.txnid,
          amount: payment.amount,
          productinfo: payment.productinfo,
          firstname: payment.firstname,
          email: payment.email,
          phone: payment.phone,
          surl: payment.surl,
          furl: payment.furl,
          curl: payment.curl,
          hash: payment.hash,
          udf1: payment.udf1 || '',
          udf2: payment.udf2 || '',
          udf3: payment.udf3 || '',
          udf4: payment.udf4 || '',
          udf5: payment.udf5 || ''
        };

        // Store order info in sessionStorage for payment result page
        sessionStorage.setItem('pendingOrderId', order.orderId);
        sessionStorage.setItem('pendingOrderNumber', order.orderNumber);

        // Submit PayU form
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = payment.payuUrl;

        Object.keys(payuData).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = payuData[key];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();

      } else {
        setError(res.data.error || 'Failed to create order');
      }
    } catch (err) {
      logger.error('❌ handlePlaceOrder error', err);
      setError(err.response?.data?.error || 'Failed to create order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="cart-container"><div className="loading-message">⏳ Loading addresses...</div></div>;

  return (
    <div className="cart-container">
      <div className="cart-header">
        <button className="back-button product-details-back-button" onClick={() => navigate(-1)} aria-label="Go back">
          <MdArrowBack size={isMobile ? 16 : 24} />
          {!isMobile && 'Back'}
        </button>
        <h1 className="cart-title">Select Delivery Address</h1>
      </div>

      {error && <div className="error-message">⚠️ {error}</div>}

      <div className="checkout-content">
        {/* LEFT: addresses + add/edit form */}
        <div>
          <div className="addresses-list">
            {addresses.length === 0 ? (
              <div className="empty-cart">
                <h3>No saved addresses</h3>
                <p>Add a new address to continue</p>
                <button className="continue-shopping-btn" onClick={() => { setShowAddForm(true); setEditingAddressId(null); }}>Add new address</button>
              </div>
            ) : (
              <div>
                {addresses.map(addr => (
                  <label key={addr.AddressId} className={`address-card ${selectedAddressId === addr.AddressId ? 'selected' : ''}`}>
                    <input type="radio" name="selectedAddress" checked={selectedAddressId === addr.AddressId} onChange={() => setSelectedAddressId(addr.AddressId)} />
                    <div className="address-content">
                      <strong>{addr.FullName}</strong>
                      <div style={{ fontSize: 13 }}>{addr.PhoneNumber}</div>
                      <div style={{ fontSize: 13, marginTop: 6 }}>{addr.AddressLine1}{addr.AddressLine2 ? `, ${addr.AddressLine2}` : ''}</div>
                      <div style={{ fontSize: 13 }}>{addr.City} · {addr.State} · {addr.PostalCode}</div>
                      <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>{addr.IsDefault ? 'Default address' : ''}</div>
                    </div>

                    <div className="address-actions">
                      <button
                        className="edit-address-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowAddForm(true);
                          setEditingAddressId(addr.AddressId);
                          setForm({
                            fullName: addr.FullName || user?.full_name || '',
                            phoneNumber: addr.PhoneNumber || user?.mobile_number || '',
                            addressLine1: addr.AddressLine1 || '',
                            addressLine2: addr.AddressLine2 || '',
                            city: addr.City || '',
                            state: addr.State || '',
                            postalCode: addr.PostalCode || '',
                            country: addr.Country || 'India',
                            isDefault: !!addr.IsDefault
                          });
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </label>
                ))}

                <div style={{ marginTop: 12 }}>
                  <button className="continue-shopping-btn" onClick={() => { setShowAddForm(true); setEditingAddressId(null); }}>+ Add new address</button>
                </div>
              </div>
            )}
          </div>

          {showAddForm && (
            <div className="add-address-form">
              <h3>{editingAddressId ? 'Edit address' : 'Add new address'}</h3>
              <form onSubmit={handleSaveAddress}>
                <div className="form-grid">
                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label">Full Name</label>
                      <input name="fullName" placeholder="Enter full name" value={form.fullName} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Phone Number</label>
                      <input name="phoneNumber" placeholder="Enter phone number" value={form.phoneNumber} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label">Address Line 1</label>
                      <input name="addressLine1" placeholder="Street address" value={form.addressLine1} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Address Line 2 (Optional)</label>
                      <input name="addressLine2" placeholder="Apartment, suite, etc." value={form.addressLine2} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label">City</label>
                      <input name="city" placeholder="City" value={form.city} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                    <div className="form-field">
                      <label className="form-label">State</label>
                      <input name="state" placeholder="State" value={form.state} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label">Postal Code</label>
                      <input name="postalCode" placeholder="Postal code" value={form.postalCode} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Country</label>
                      <input name="country" placeholder="Country" value={form.country} onChange={handleInput} className="form-input form-input-enhanced" />
                    </div>
                  </div>
                </div>

                <div className="form-checkbox-group">
                  <label className="checkbox-label">
                    <input type="checkbox" name="isDefault" checked={form.isDefault} onChange={handleInput} className="checkbox-input" /> 
                    <span>Set as default address</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={saving}>{saving ? '⏳ Saving...' : (editingAddressId ? '💾 Save Changes' : '✓ Save Address')}</button>
                  <button type="button" className="btn-secondary" onClick={() => { setShowAddForm(false); setError(null); setEditingAddressId(null); }}>✕ Cancel</button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* RIGHT: Order summary */}
        <div className="order-summary">
          <h2 className="summary-title">Order Summary</h2>

          {summaryLoading ? (
            <div className="loading-message">⏳ Loading summary...</div>
          ) : (
            <>
              <div className="summary-row">
                <span className="summary-label">Items</span>
                <span className="summary-value">{cartItems.length}</span>
              </div>

              {/* Small itemized list */}
              <div style={{ margin: '8px 0 12px', maxHeight: 160, overflow: 'auto' }}>
                {cartItems.map(it => (
                  <div key={it.UserCartDetailId} style={{ display: 'flex', justifyContent: 'space-between', color: '#dcdcdc', fontSize: 13, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                    <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.ProductName} × {it.Quantity}</div>
                    <div>₹{(it.Price * it.Quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>

              <div className="summary-row">
                <span className="summary-label">Subtotal</span>
                <span className="summary-value">₹{cartItems.reduce((s, i) => s + (i.Price * i.Quantity), 0).toFixed(2)}</span>
              </div>

              <div className="summary-row">
                <span className="summary-label">Tax (18% GST)</span>
                <span className="summary-value">₹{(cartItems.reduce((s, i) => s + (i.Price * i.Quantity), 0) * 0.18).toFixed(2)}</span>
              </div>

              <div className="summary-row shipping">
                <span className="summary-label">Shipping</span>
                <span className="summary-value free">FREE</span>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-row total">
                <span className="summary-label">Total Amount</span>
                <span className="summary-value">₹{(cartItems.reduce((s, i) => s + (i.Price * i.Quantity), 0) * 1.18).toFixed(2)}</span>
              </div>

              <button
                className="checkout-btn"
                onClick={handlePlaceOrder}
                disabled={saving || !selectedAddressId || cartItems.length === 0}
                title={!selectedAddressId ? 'Select an address' : cartItems.length === 0 ? 'Cart is empty' : 'Place order and proceed to payment'}
              >
                {saving ? '⏳ Creating Order...' : 'Place Order & Pay'}
              </button>

              <div className="security-info">
                <p>🔒 Secure checkout with encrypted payment</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Checkout;
